import { Injectable } from '@angular/core';
import { GlobalService, UnsubscribeOnDestroyAdapter, LogService } from '@medlogic/shared/shared-interfaces';
import { WebService } from './web.service';
import { Observable } from 'rxjs';
import { refCount, publishReplay } from 'rxjs/operators';

@Injectable()
export class ObjectDAL extends UnsubscribeOnDestroyAdapter {

  protected itemsCache: Observable<any>;
  protected currentKeys: any[] = new Array<any>();

  constructor(
    protected webService: WebService,
    protected log: LogService,
    protected global: GlobalService,
  ) {
    super();
  }

  /**
   * Busca os dados usando cache para evitar chamadas desnecessárias ao backend.
   * @param getItemsMethod O método a ser chamado para buscar os dados, que deve retornar um Observable.
   * @param keys As chaves usadas para identificar os dados a serem buscados, que serão comparadas com as chaves atuais para decidir se deve buscar os dados novamente ou usar o cache.
   * @returns Um Observable com os dados buscados, que pode ser do tipo ICadastroListaDAL ou qualquer outro tipo dependendo do método chamado.
   */
  protected getWithCache<T>(getItemsMethod: Function, ...keys): Observable<T> {
    try {
      if (this.isDiferentKey(keys) || (!this.itemsCache)) {
        this.itemsCache = getItemsMethod.apply(this, keys)
          .pipe(
            publishReplay(),
            refCount()
          )
      }

      return this.itemsCache;
    } catch (error) {
      this.log.Registrar('ObjectDAL', 'getWithCache', error.message);
    }

    return null;
  }

  /**
   * Verifica se as chaves são diferentes das atuais para decidir se deve buscar os dados novamente ou usar o cache.
   * @param keys As chaves a serem comparadas com as chaves atuais, que são usadas para identificar os dados a serem buscados.
   * @returns true se as chaves forem diferentes das atuais, false caso contrário.
   */
  protected isDiferentKey(...keys): boolean {
    try {
      let isEqual = true;

      if (keys.length !== this.currentKeys.length) {
        return true;
      }

      keys.forEach((k, i) => {
        if (this.currentKeys[i] !== k) {
          isEqual = false;
        }
      });

      return !isEqual;
    } catch (error) {
      this.log.Registrar('ObjectDAL', 'isDiferentKey', error.message);
    }
  }

  /**
   * Apaga o cache específico.
   * A princípio, não há como saber se o método foi getCadastroFiltrado ou getCadastroLista.
   * @param method O método do cache a ser limpo, que pode ser getCadastroFiltrado ou getCadastroLista.
   * @param params Os parâmetros usados para identificar o cache a ser limpo, que devem ser os mesmos usados para buscar os dados.
   */
  cleanCache(method: string, params: Array<any>): void {
    try {
      this.webService.cleanCacheFromKey(method, params);
      this.itemsCache = null;
      this.currentKeys = new Array<any>();
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'cleanCache', error.message);
    }
  }
}
